import { createSlice } from '@reduxjs/toolkit';
import { auditLogsApi, globalApi } from '@store/rtk';
import { TableData } from '@uikit/Tables/types';
import { formatUTCToLocal } from '@src/utils';
import { TABLE_COLUMNS } from '@uikit/Tables/columns';
import { sessionSetter } from '@utils/storage';

const REPORT_FILTERS_STORAGE_KEY = 'rdf';

type PersistedReportFilters = {
  dashboardChartQuery: any;
  selectedDateIDForChart: number;
  activeDateClass: string;
};

const persistReportFilters = (state: {
  dashboardChartQuery: any;
  selectedDateIDForChart: number;
  activeDateClass: string;
}) => {
  const { contractorIds, ...queryWithoutContractors } =
    state.dashboardChartQuery ?? {};
  sessionSetter.setItem(REPORT_FILTERS_STORAGE_KEY, {
    dashboardChartQuery: queryWithoutContractors,
    selectedDateIDForChart: state.selectedDateIDForChart,
    activeDateClass: state.activeDateClass,
  });
};

const persistedReportFilters = sessionSetter.getItem<PersistedReportFilters>(
  REPORT_FILTERS_STORAGE_KEY
);

type globalSliceType = {
  currentPath: string;
  currentLocation: string;
  toggleNav: boolean;
  updateImageState: { success: boolean; message: string };
  recentActivity: {
    content: any[];
    [key: string]: any;
  };
  segmentTransmitData: TableData;
  monitoringTransmitData: TableData;
  dashboardChartQuery: any;
  defaultValuesForCharts: any;
  selectedDateIDForChart: number;
  activeDateClass: string;
  // | string
  // | {
  //     startDate: Date | null | string;
  //     endDate: Date | null | string;
  //   };
  pizzaChartStatus: {
    month: number;
    year: number;
  };
};

const { segmentTransmitColumns, monitoringTransmitColumns } = TABLE_COLUMNS;

const initialState: globalSliceType = {
  currentPath: '',
  currentLocation: '',
  defaultValuesForCharts: {},
  selectedDateIDForChart: persistedReportFilters?.selectedDateIDForChart ?? 0,
  toggleNav: true,
  updateImageState: { success: false, message: '' },
  dashboardChartQuery: persistedReportFilters?.dashboardChartQuery
    ? {
        ...persistedReportFilters.dashboardChartQuery,
        contractorIds: undefined,
      }
    : { range: 'today' },
  recentActivity: {
    content: [],
    number: 0,
    last: false,
  },
  activeDateClass: persistedReportFilters?.activeDateClass ?? ' active',
  segmentTransmitData: {
    columns: segmentTransmitColumns,
    rows: [],
    size: 5,
  },
  monitoringTransmitData: {
    columns: monitoringTransmitColumns,
    rows: [],
    size: 5,
  },
  pizzaChartStatus: {
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  },
};

const handleCurrentPath: Record<string, string> = {
  '': 'home',
  payors: 'payor',
  members: 'member',
  administrator: 'user',
  places: 'place',
  contractors: 'contractor',
  rides: 'ride',
  'standing-order': 'standingOrder',
  monitoring: 'monitoring',
  assignment: 'assignment',
  reporting: 'reporting',
  profile: 'profile',
  //TODO this for only contractor portal version
  // check this and then remove if not needed
  admin: 'contractor',
  // without it usePermissions stays loading on every credentialing page
  credentialing: 'contractor',
};

const getPathName = (pathname: string) =>
  pathname.slice(1, pathname.slice(1).indexOf('/') + 1);

export const getCurrentPathKey = (pathname: string) =>
  handleCurrentPath[getPathName(pathname)];

const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    setDefaultValuesForCharts: (
      state: globalSliceType,
      {
        payload,
      }: {
        payload: {
          range?: string;
          startDate: Date | null | string;
          endDate: Date | null | string;
        };
      }
    ) => {
      state.defaultValuesForCharts = payload;
    },
    setSelectedDateIDForChart: (
      state: globalSliceType,
      { payload }: { payload: number }
    ) => {
      state.selectedDateIDForChart = payload;
      persistReportFilters(state);
    },
    setActiveDateClass: (
      state: globalSliceType,
      { payload }: { payload: string }
    ) => {
      state.activeDateClass = payload;
      persistReportFilters(state);
    },
    setPizzaChartStatus: (
      state: globalSliceType,
      { payload }: { payload: any }
    ) => {
      state.pizzaChartStatus = payload;
    },
    setRecentActivity: (
      state: globalSliceType,
      { payload }: { payload: any }
    ) => {
      state.recentActivity = payload;
    },
    setDashboardChartQuery: (
      state: globalSliceType,
      { payload }: { payload: any }
    ) => {
      state.dashboardChartQuery = payload;
      persistReportFilters(state);
    },
    setStatusUploadImage: (
      state: globalSliceType,
      { payload }: { payload: { success: boolean; message: string } }
    ) => {
      state.updateImageState = payload;
    },
    setCurrentPath(state) {
      state.currentLocation = getPathName(location.pathname);
      state.currentPath = getCurrentPathKey(location.pathname);
    },
    setToggleNav(state, { payload }) {
      state.toggleNav = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      globalApi.endpoints.getRecentActivity.matchFulfilled,
      (state, { payload }) => {
        const data =
          payload.number === 0
            ? payload.content
            : [...state.recentActivity.content, ...payload.content];

        state.recentActivity = {
          ...payload,
          content: data,
          requestForInfiniteScroll: globalApi.endpoints.getRecentActivity,
        };
      }
    );
    builder.addMatcher(
      auditLogsApi.endpoints.getPortalLogs.matchFulfilled,
      (state, { payload }) => {
        const data =
          payload.number === 0
            ? payload.content
            : [...state.recentActivity.content, ...payload.content];

        state.recentActivity = {
          ...payload,
          content: data,
          requestForInfiniteScroll: auditLogsApi.endpoints.getPortalLogs,
        };
      }
    );
    builder.addMatcher(
      globalApi.endpoints.getSegmentTransmit.matchFulfilled,
      (state, { payload }) => {
        if (payload.content && Array.isArray(payload.content)) {
          const rows = [
            ...payload.content.map((el) => ({
              ...el,
              time: formatUTCToLocal(el.time),
            })),
          ];

          state.segmentTransmitData = {
            ...state.segmentTransmitData,
            ...payload,
            rows,
            requestTablePageChange: globalApi.endpoints.getSegmentTransmit,
          };

          state.monitoringTransmitData = {
            ...state.monitoringTransmitData,
            ...payload,
            rows,
            requestTablePageChange: globalApi.endpoints.getSegmentTransmit,
          };
        }
      }
    );
  },
});

export const selectGlobal = (state: { global: globalSliceType }) =>
  state.global;

export const {
  setCurrentPath,
  setStatusUploadImage,
  setDashboardChartQuery,
  setSelectedDateIDForChart,
  setRecentActivity,
  setToggleNav,
  setPizzaChartStatus,
  setDefaultValuesForCharts,
  setActiveDateClass,
} = globalSlice.actions;
export const globalReducer = globalSlice.reducer;
